import React, { useMemo } from 'react';
import { useGetWorkspaceFromSurveyId } from '~/behaviors/react-query/integration';
import { mui } from '~/tools/mui';
import { urls } from '~/tools/urls';
import Loading from '~/views/UI.shared/Loading';
import { useGetRedirectToParam } from './useGetRedirectToParam';
import { extractSurveyId, makeKazooUrlWithRedirect } from './KazooWebAuthRedirect.helpers.ts';

/**
 * This component redirects users to KW when they are from an integrated company.
 *
 * We check that by looking into the URL and looking for the redirectTo query string.
 * We extract the survey ID from the query string and check if the company is integrated.
 * If the company is integrated, we redirect them to KW.
 *
 * > OBS: That only happens if the user is not logged in.
 * > That is why this component needs to be after <CheckAuth>
 *
 * TD;LR:
 * - URL -> redirectTo query string -> has survey id -> company is integrated -> redirect to KW
 * - Only done if the person is not logged in
 *
 * @param children
 * @constructor
 */
export function KazooWebAuthRedirect({ children }) {
  const redirectUrl = useGetRedirectToParam();
  const safeRedirectUrl = useMemo(() => {
    if (!redirectUrl || !urls.isSafeRelativePath(redirectUrl)) return null;

    return redirectUrl;
  }, [redirectUrl]);

  const surveyId = useMemo(() => {
    return extractSurveyId(safeRedirectUrl);
  }, [safeRedirectUrl]);

  // surveyId! is alright, as shouldCheckWorkspace can only be true if there is a surveyId
  const { data: workspace, isLoading } = useGetWorkspaceFromSurveyId({
    surveyId: surveyId!,
    shouldRun: !!surveyId,
  });

  // `safeRedirectUrl` and `surveyId` will always be true if `workspace` is defined,
  // but this is to help with typing
  const shouldRedirect = workspace && surveyId && safeRedirectUrl;

  if (shouldRedirect) {
    const kazooBaseUrl = urls.createKazooBaseUrl(workspace);
    if (!kazooBaseUrl) {
      console.warn('Environment variable not found for Kazoo URL.');
      return children;
    }

    const kazooRedirectUrl = makeKazooUrlWithRedirect({ kazooBaseUrl, redirectUrl: safeRedirectUrl });
    window.location.href = kazooRedirectUrl;
  }

  if (shouldRedirect || isLoading) {
    return (
      <mui.Box
        data-testid="loading-container"
        component="main"
        sx={(theme) => ({
          height: '100dvh',
          width: '100dvw',
          background: theme.legacyPalette.darkaccent,
        })}
      >
        <Loading show containerHeight="100%" />
      </mui.Box>
    );
  }

  return children;
}
